import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

interface ReadStatusContextType {
  readContactRequests: string[];
  markAsRead: (contactRequestId: string) => void;
  markAsUnread: (contactRequestId: string) => void;
  isRead: (contactRequestId: string) => boolean;
  clearAllReadStatus: () => void;
  getUnreadCount: (contactRequests: { id: string }[]) => number;
  isLoaded: boolean;
}

const ReadStatusContext = createContext<ReadStatusContextType | undefined>(
  undefined,
);

const STORAGE_KEY = "contactRequests_readStatus";

interface ReadStatusProviderProps {
  children: ReactNode;
}

export const ReadStatusProvider: React.FC<ReadStatusProviderProps> = ({
  children,
}) => {
  const [readContactRequests, setReadContactRequests] = useState<string[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const readIds = JSON.parse(stored) as string[];
        setReadContactRequests(readIds || []);
      }
    } catch (error) {
      console.warn("Failed to load read status from localStorage:", error);
    } finally {
      setIsLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!isLoaded) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(readContactRequests));
    } catch (error) {
      console.warn("Failed to save read status to localStorage:", error);
    }
  }, [readContactRequests, isLoaded]);

  const markAsRead = (contactRequestId: string) => {
    setReadContactRequests((prev) => {
      if (prev.includes(contactRequestId)) {
        return prev;
      }
      return [...prev, contactRequestId];
    });
  };

  const markAsUnread = (contactRequestId: string) => {
    setReadContactRequests((prev) =>
      prev.filter((id) => id !== contactRequestId),
    );
  };

  const isRead = (contactRequestId: string): boolean => {
    return readContactRequests.includes(contactRequestId);
  };

  const clearAllReadStatus = () => {
    setReadContactRequests([]);
  };

  const getUnreadCount = (contactRequests: { id: string }[]): number => {
    return contactRequests.filter(
      (request) => request.id && !readContactRequests.includes(request.id),
    ).length;
  };

  const value: ReadStatusContextType = {
    readContactRequests,
    markAsRead,
    markAsUnread,
    isRead,
    clearAllReadStatus,
    getUnreadCount,
    isLoaded,
  };

  return (
    <ReadStatusContext.Provider value={value}>
      {children}
    </ReadStatusContext.Provider>
  );
};

export const useReadStatus = (): ReadStatusContextType => {
  const context = useContext(ReadStatusContext);
  if (context === undefined) {
    throw new Error("useReadStatus must be used within a ReadStatusProvider");
  }
  return context;
};
